import { useState } from 'react';
import { Typography, Tabs, Card, Descriptions, Collapse, Tag, Space, List, Button, message, Alert } from 'antd';
import {
  InfoCircleOutlined,
  NodeIndexOutlined,
  AuditOutlined,
  PartitionOutlined,
  ExperimentOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import axiosClient from '@/api/axiosClient';
import {
  PHASE_ORDER,
  getPhaseLabel,
  getPhaseGateLabel,
  getPhaseDescription,
  getPhaseDeliverables,
  getPhaseColor,
  getPhaseOwnerRole,
} from '@/utils/phaseHelpers';
import AuditLogViewer from '@/components/admin/AuditLogViewer';
import PageHeader from '@/components/ui/PageHeader';
import type { RfoPhase } from '@/types/workflow';

const { Text, Paragraph } = Typography;

const OverviewTab = () => (
  <div>
    <Card title="Application Information" style={{ marginBottom: 16 }}>
      <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
        <Descriptions.Item label="Application Name">
          RFO Response System
        </Descriptions.Item>
        <Descriptions.Item label="Version">1.0.0</Descriptions.Item>
        <Descriptions.Item label="Environment">
          <Tag color="blue">Production</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Authentication">
          Keycloak SSO
        </Descriptions.Item>
        <Descriptions.Item label="Database">PostgreSQL</Descriptions.Item>
        <Descriptions.Item label="Storage">MinIO Object Storage</Descriptions.Item>
        <Descriptions.Item label="API Version">v1</Descriptions.Item>
        <Descriptions.Item label="Frontend Framework">
          React 18 + Ant Design
        </Descriptions.Item>
      </Descriptions>
    </Card>

    <Card title="System Architecture">
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Backend">
          Spring Boot 3.x with Java 17
        </Descriptions.Item>
        <Descriptions.Item label="Frontend">
          React 18 + TypeScript + Vite
        </Descriptions.Item>
        <Descriptions.Item label="Authentication Provider">
          Keycloak (OpenID Connect)
        </Descriptions.Item>
        <Descriptions.Item label="File Storage">
          MinIO (S3-compatible)
        </Descriptions.Item>
        <Descriptions.Item label="Search">
          PostgreSQL Full-Text Search
        </Descriptions.Item>
        <Descriptions.Item label="Workflow Engine">
          Custom Phase-Gate Engine (6 gates)
        </Descriptions.Item>
      </Descriptions>
    </Card>
  </div>
);

const PhaseGatesTab = () => {
  const collapseItems = PHASE_ORDER.map((phase: RfoPhase) => ({
    key: phase,
    label: (
      <Space>
        <Tag color={getPhaseColor(phase)} style={{ fontWeight: 'bold' }}>
          {phase.replace('PHASE_', 'Gate ')}
        </Tag>
        <Text strong>{getPhaseLabel(phase)}</Text>
      </Space>
    ),
    children: (
      <div>
        <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
          <Descriptions.Item label="Phase Name">
            {getPhaseLabel(phase)}
          </Descriptions.Item>
          <Descriptions.Item label="Gate Label">
            {getPhaseGateLabel(phase)}
          </Descriptions.Item>
          <Descriptions.Item label="Owner Role" span={2}>
            {getPhaseOwnerRole(phase)}
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            {getPhaseDescription(phase)}
          </Descriptions.Item>
        </Descriptions>
        <div style={{ marginTop: 12 }}>
          <Text strong>Deliverables:</Text>
          <List
            size="small"
            dataSource={getPhaseDeliverables(phase)}
            renderItem={(item: string) => (
              <List.Item style={{ padding: '4px 0' }}>
                <Text>- {item}</Text>
              </List.Item>
            )}
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
    ),
  }));

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        The RFO response process follows a 7-phase gate workflow. Each phase has
        specific deliverables and gate criteria that must be met before advancing
        to the next phase.
      </Paragraph>
      <Collapse items={collapseItems} defaultActiveKey={['PHASE_A']} />
    </div>
  );
};

const WorkflowTab = () => (
  <div>
    <Card title="Phase Progression Rules" style={{ marginBottom: 16 }}>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Direction">
          Phases progress sequentially from A to G
        </Descriptions.Item>
        <Descriptions.Item label="Gate Requirements">
          Each phase has gate requirements that must be met before advancing
        </Descriptions.Item>
        <Descriptions.Item label="Advancement">
          Only authorized roles can advance a workflow to the next phase
        </Descriptions.Item>
        <Descriptions.Item label="No-Bid Exit">
          A No-Bid decision at Phase B terminates the workflow
        </Descriptions.Item>
        <Descriptions.Item label="Status Tracking">
          Workflow status can be ACTIVE, ON_HOLD, WON, LOST, NO_BID, or CANCELLED
        </Descriptions.Item>
      </Descriptions>
    </Card>

    <Card title="Phase Flow">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {PHASE_ORDER.map((phase, index) => (
          <Space key={phase} size={4}>
            <Tag
              color={getPhaseColor(phase)}
              style={{ fontSize: 13, padding: '4px 12px' }}
            >
              {phase.replace('PHASE_', '')} - {getPhaseLabel(phase)}
            </Tag>
            {index < PHASE_ORDER.length - 1 && (
              <Text type="secondary" style={{ fontSize: 18 }}>
                →
              </Text>
            )}
          </Space>
        ))}
      </div>
    </Card>

    <Card title="Role Responsibilities" style={{ marginTop: 16 }}>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Opportunity Finder">
          Discovers and intakes new opportunities (Phase A owner)
        </Descriptions.Item>
        <Descriptions.Item label="Enterprise Architect">
          Assesses technical fit and eligibility (Phase B owner)
        </Descriptions.Item>
        <Descriptions.Item label="BD Manager">
          Manages proposal planning and post-submission (Phase C, G owner)
        </Descriptions.Item>
        <Descriptions.Item label="Technical Writer">
          Builds compliance matrix and drafts content (Phase D owner)
        </Descriptions.Item>
        <Descriptions.Item label="Cloud Architect">
          Reviews technical aspects and quality (Phase E owner)
        </Descriptions.Item>
        <Descriptions.Item label="CEO">
          Provides executive sign-off for major decisions
        </Descriptions.Item>
        <Descriptions.Item label="QC Lead">
          Performs quality control reviews during Phase E
        </Descriptions.Item>
        <Descriptions.Item label="Resourcing Manager">
          Manages resource allocation and staffing feasibility
        </Descriptions.Item>
      </Descriptions>
    </Card>
  </div>
);

const AuditLogTab = () => (
  <div>
    <AuditLogViewer />
  </div>
);

const AIToolsTab = () => {
  const [extracting, setExtracting] = useState(false);

  const handleBatchExtract = async () => {
    setExtracting(true);
    try {
      // Use a dummy opportunity ID — the batch endpoint ignores it
      await axiosClient.post('/opportunities/00000000-0000-0000-0000-000000000000/analysis/extract-rfo-requirements-all');
      message.success('Batch extraction started. You will receive notifications as each opportunity is processed.');
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to start batch extraction');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div>
      <Card title="RFO Requirements Extraction" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Text>
            AI automatically extracts structured requirements from uploaded RFO documents at Gate A.
            New uploads trigger extraction automatically. Use the button below to run extraction for
            all existing opportunities that don't have extracted requirements yet.
          </Text>
          <Alert
            message="This processes all existing opportunities with RFO documents. Each extraction uses the Claude API and takes ~30 seconds. A 5-second gap is added between each to respect rate limits."
            type="info"
            showIcon
          />
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={extracting}
            onClick={handleBatchExtract}
            style={{ background: 'var(--color-purple)', borderColor: 'var(--color-purple)' }}
          >
            Extract RFO Requirements for All Existing Opportunities
          </Button>
        </Space>
      </Card>
    </div>
  );
};

const SystemSettingsPage = () => {
  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <InfoCircleOutlined /> Overview
        </span>
      ),
      children: <OverviewTab />,
    },
    {
      key: 'phases',
      label: (
        <span>
          <NodeIndexOutlined /> Phase Gates
        </span>
      ),
      children: <PhaseGatesTab />,
    },
    {
      key: 'workflow',
      label: (
        <span>
          <PartitionOutlined /> Workflow
        </span>
      ),
      children: <WorkflowTab />,
    },
    {
      key: 'ai-tools',
      label: (
        <span>
          <ExperimentOutlined /> AI Tools
        </span>
      ),
      children: <AIToolsTab />,
    },
    {
      key: 'audit',
      label: (
        <span>
          <AuditOutlined /> Audit Log
        </span>
      ),
      children: <AuditLogTab />,
    },
  ];

  return (
    <div>
      <PageHeader
        title="System Settings"
        subtitle="Application configuration, phase-gate workflow, AI tools, and audit log"
      />
      <Tabs items={tabItems} defaultActiveKey="overview" />
    </div>
  );
};

export default SystemSettingsPage;
